import http from "@/composables/axios";
import { defineStore } from "pinia";
import _ from "lodash";
import { formatToLocalDate } from "@/helpers";
import { useAuthStore } from "./auth";
import { storeToRefs } from 'pinia';

export const useFixturesStore = defineStore('fixtures', {
    state: () => ({
        fixtures: [],
        sports: [],
        liveSports: [], 
        regions: [],
        competitions: [],
        mainCompetitions:[],
        mainFixtures:[],
        groupedByDatePreEvents:{}, 
        availablePreDates:[],
        marketGroups:[],
        allMarkets: [],
        allCompetitions:[],
        selections:[],
        scoreboards:[],
        eventsStatuses: {
            0: 'In Progress',
            1: 'NSY',
            2: 'Finished',
            3: 'Pre Finished',
            4: 'Coverage Lost',
            5: 'Suspended'
        }
    }),

    getters: {
        // Sports the signed-in user may not see (user.blcsp pre-match, user.blcsl
        // live). A sport can be blocked for one phase and open for the other, so
        // a fixture is judged by the phase it is in right now. `state.fixtures`
        // stays complete — the betslip and bet history still resolve events from
        // it — and every list the user browses reads `visibleFixtures` instead.
        blockedSports() {
            const user = useAuthStore().user
            return {
                pre: new Set((user?.blcsp ?? []).map(Number)),
                live: new Set((user?.blcsl ?? []).map(Number)),
            }
        },
        isFixtureBlocked() {
            const blocked = this.blockedSports
            return (f) => !!f && (f.s === 0 ? blocked.live : blocked.pre).has(Number(f.si))
        },
        isPreSportBlocked() {
            const blocked = this.blockedSports
            return (sportId) => blocked.pre.has(Number(sportId))
        },
        visibleFixtures(state) {
            const blocked = this.blockedSports
            if (!blocked.pre.size && !blocked.live.size) return state.fixtures
            return state.fixtures.filter(f => !this.isFixtureBlocked(f))
        },
        // For an event page opened by URL: null when the event's sport is blocked.
        getVisibleEvent() {
            return (eventId) => this.visibleFixtures.find(f => f.i == eventId) || null
        },
        getFixtures() { return this.visibleFixtures },
        getLiveEvents() { return this.visibleFixtures.filter(fixture => fixture.s === 0) },
        getPreEvents() { return this.visibleFixtures.filter(fixture => fixture.s === 1) },
        getSports: (state) => state.sports,
        // The pre-match sports menu.
        getPreSports(state) { return state.sports.filter(s => !this.isPreSportBlocked(s.si)) },
        getRegions: (state) => state.regions,    
        getCompetitions: (state) => {
        if (!Object.keys(state.allCompetitions).length) return state.competitions
            return [...state.competitions].sort((a, b) => 
                (state.allCompetitions[a.ci] ?? 9999) - (state.allCompetitions[b.ci] ?? 9999)
            )
        },
        getLiveSports(state) {
            const liveSportIds = [...new Set(
                this.visibleFixtures.filter(f => f.s === 0).map(f => f.si)
            )];
            return state.sports.filter(s => liveSportIds.includes(s.si));
        },
        upcomingEvents() {
            return (sportId) => this.visibleFixtures
            .filter(f => f.s !== 0 && f.si == sportId) // only pre + sportId
            .sort((a, b) => new Date(a.t) - new Date(b.t)) 
            .slice(0, 10); 
        },
        tenEvents() {
            return (sportId) => this.visibleFixtures
            .filter(f => f.s === 0 && f.si == sportId) // only live + sportId
            .sort((a, b) => new Date(a.t) - new Date(b.t))  
            .slice(0, 10);                                 
        },
        // Featured leagues list pre-match events, so a league whose pre-match
        // events are all in a blocked sport is dropped rather than shown empty.
        getMainCompetitions(state) {
            const allPre = new Set()
            const visiblePre = new Set()
            for (const f of state.fixtures) if (f.s === 1) allPre.add(String(f.ci))
            for (const f of this.visibleFixtures) if (f.s === 1) visiblePre.add(String(f.ci))
            const list = state.mainCompetitions.filter(c => !allPre.has(String(c.ci)) || visiblePre.has(String(c.ci)))
            if (!Object.keys(state.allCompetitions).length) return list
                return [...list].sort((a, b) =>
                (state.allCompetitions[a.ci] ?? 9999) - (state.allCompetitions[b.ci] ?? 9999)
            )
        },
        getMainFixtures(state) {
            return (sportId = null) => {
                const list = state.mainFixtures.filter(f => !this.isFixtureBlocked(f))
                return sportId ? list.filter(f => f.si == sportId) : list
            }
        },
        eventsByCompetition() {
            return (competitionId) => this.visibleFixtures
                .filter(f => f.ci == competitionId && f.s === 1) // only pre events
                .sort((a, b) => new Date(a.t) - new Date(b.t)); 
        },
        getCompetition: (state) => (ci) => {
            return state.competitions.find(c => c.ci == ci) || null;
        },
        getSport: (state) => (si) => {
            return state.sports.find(s => s.si == si || null) 
        },
        getGroupedPreEventsByDate: (state) => (date) => {
            if (!state.groupedByDatePreEvents) return [];

            const eventsForDate = state.groupedByDatePreEvents[date] || [];
            const groupedByCompetition = _.groupBy(eventsForDate, 'ci');

            return Object.keys(groupedByCompetition).map(ci => {
                const events = groupedByCompetition[ci];
                    return {
                        ci,
                        cn: events[0]?.cn || '',
                        events
                    };
            }).sort(
                (a, b) =>
                (state.allCompetitions[Number(a.ci)] ?? 9999) -
                (state.allCompetitions[Number(b.ci)] ?? 9999)
            )
        },
        getAvailablePreDates: state => state.availablePreDates,
        getCompetitionsByRegion(state) { return (sportId) => {
            // the pre-match sport pages: nothing at all for a sport blocked pre-match
            if (this.isPreSportBlocked(sportId)) return []
            const filteredFixtures = this.visibleFixtures.filter(f => f.si == sportId);

            const groupedByRegion = _.groupBy(filteredFixtures, 'ri');

            return Object.keys(groupedByRegion).map(regionId => {
                const regionFixtures = groupedByRegion[regionId];

                const competitionsGrouped = _.groupBy(regionFixtures, 'ci');

                const competitions = Object.keys(competitionsGrouped).map(compId => {
                const compFixtures = competitionsGrouped[compId];
                return {
                    ci: compId,
                    cn: compFixtures[0]?.cn || '',
                    count: compFixtures.length
                };
            }).sort((a, b) =>(state.allCompetitions[Number(a.ci)] ?? 9999) - (state.allCompetitions[Number(b.ci)] ?? 9999));
                return {
                    ri: regionId,
                    rn: regionFixtures[0]?.rn || '',
                    competitions
                };
            });
        } },
        hasFixtures: (state) => {
            return state.fixtures && state.fixtures.length > 0;
        },
        getEvent: (state) => (eventId) => {
            return state.fixtures.find(f => f.i == eventId) || null;
        },
        getGroups: (state) => state.marketGroups,
        getSportMarketGroups: (state) => (sportId)=>{ return state.marketGroups.filter(g => g.si == sportId)},
        getLiveEventsBySport(state) { return (sportId) => {
            const events = this.visibleFixtures.filter(f => f.s == 0 && f.si == sportId);

            const grouped = {};
            const seen = new Set();
            for (const ev of events) {
                if (seen.has(ev.i)) continue;
                seen.add(ev.i);
                if (!grouped[ev.ci]) {
                grouped[ev.ci] = {
                    ci: ev.ci,
                    cn: ev.cn,
                    rn: ev.rn,
                    ri: ev.ri,
                    events: []
                };
                }
                grouped[ev.ci].events.push(ev);
            }

            return Object.values(grouped).sort(
                (a, b) =>
                (state.allCompetitions[Number(a.ci)] ?? 9999) -
                (state.allCompetitions[Number(b.ci)] ?? 9999)
            );
        } },

        searchForLiveEvents() { return (query, sport) => {
            if (!sport || !query.trim()) return [];

            const q = query.toLowerCase();
            const events = this.visibleFixtures.filter(f => f.s === 0 && f.si === sport.si);

            return events.filter(e => 
                (e.h && e.h.toLowerCase().includes(q)) || 
                (e.a && e.a.toLowerCase().includes(q))
            );
        } },
        getAllMarkets: (state) => state.allMarkets,
        getMarket: (state) => (marketId) => {
            return state.allMarkets.find(m => m.i == marketId) || null
        },
    },

    actions: {
        parseFixtureString(fixtureStr) {
            const fields = fixtureStr.split("|");
            return {
                i: fields[0] || null,        // eventId
                pi: fields[1] || null,       // providerId
                t: Number(fields[2]) || null,        // startDate
                o: fields[3] || null,        // outright
                s: Number(fields[4]) || 0,   // status si number
                si: fields[5] || null,       // sportId
                sn: fields[6] || null,       // sportName
                ri: fields[7] || null,       // regionId
                rn: fields[8] || null,       // regionName
                ci: fields[9] || null,      // competitionId
                cn: fields[10] || null,      // competitionName
                h: fields[11] || null,       // homeTeamName
                hi: fields[12] || null,      // homeTeamId
                a: fields[13] || null,       // awayTeamName
                ai: fields[14] || null,      // awayTeamId
                mnr: fields[15] || null,    //marketsLength
                onr: fields[16] || null,      //selectionsLength
                ac: fields[17] === "true",          // active event
            };
        },
        parseMarketString(marketStr) {
            const fields = marketStr.split("|")
            return {
                i: Number(fields[0]) || null,       // id
                n: fields[1] || "",                 // name
                si: Number(fields[2]) || null,      // sportId
                u: fields[3] || null,               // unique name
                t: fields[4] || null,               // type (pre/live)
                c: Number(fields[5]) || null,       // columns
                s: Number(fields[6]) || null,       // sort
                ca: fields[7] || null,              // category
                ac: fields[8] === "true",           // active
                sysnr: Number(fields[9]) || null,   // system number
                isas: fields[10] === "true",        // is asiatike
                is_a: fields[11] === "true",        // is auto settlement
                crdop: fields[12] === "true",       // card open
            }
        },
        parseMainComp(compStr){
            const fields = compStr.split("|")
             return {
                i: Number(fields[0]) || null, // id
                logo: fields[1] || "", 
             }  
        },
        async fetchFixtures() {
            await http.get(`07/fixtures`).then(res => {
                if (res.status == 200) {
                    const sportsMap = {};
                    const regionsMap = {};
                    const competitionsMap = {};
                    const seenEventIds = new Set();
                    const dedupedFixtures = [];

                    for (const fixtureStr of res.data) {
                        // the list occasionally carries a null entry; one must not
                        // throw away every other fixture
                        if (typeof fixtureStr !== 'string') continue
                        const f = this.parseFixtureString(fixtureStr);
                        if (f.si && !sportsMap[f.si]) {
                            sportsMap[f.si] = { si: f.si, sn: f.sn };
                        }
                        if (f.ri && !regionsMap[f.ri]) {
                            regionsMap[f.ri] = { ri: f.ri, rn: f.rn };
                        }
                        if (f.ci && !competitionsMap[f.ci]) {
                            competitionsMap[f.ci] = { ci: f.ci, cn: f.cn };
                        }
                        if (f.ac !== true) continue;
                        if (f.i != null) {
                            if (seenEventIds.has(f.i)) continue;
                            seenEventIds.add(f.i);
                        }
                        dedupedFixtures.push(f);
                    }
                    this.fixtures = dedupedFixtures;
                    this.sports = Object.values(sportsMap);
                    this.regions = Object.values(regionsMap);
                    this.competitions = Object.values(competitionsMap).sort(
                        (a, b) =>
                            (this.allCompetitions[Number(a.ci)] ?? 9999) -
                            (this.allCompetitions[Number(b.ci)] ?? 9999)
                    );
                }
            })
        },
        async fetchMainCompetitions() {
            await http.get('07/main-competitions').then(res => {
                if (res.status === 200) {       
                const comps = res.data.map(compStr => this.parseMainComp(compStr))
                this.mainCompetitions = comps
                    .map(co => {
                    const match = this.competitions.find(c => c.ci == co.i)
                    return match ? { ...match, logo: co.logo } : null
                    })
                    .filter(Boolean)
                    .sort(
                    (a, b) =>
                        (this.allCompetitions[Number(a.ci)] ?? 9999) -
                        (this.allCompetitions[Number(b.ci)] ?? 9999)
                    )
                }
            })
        },
        async fetchMainFixtures(){
            await http.get('07/main-fixtures').then(res =>{
                if(res.status == 200){
                    const uniqueIds = Array.from(new Set(res.data))
                    this.mainFixtures = uniqueIds
                        .map(i => this.fixtures.find(f => f.i == i))
                        .filter(Boolean);
                }
            })
        },
        groupPreEventsByDate (sportId) {
            const preEvents = this.visibleFixtures
                .filter(f => f.s === 1 && f.si == sportId)
                .sort((a, b) => a.t - b.t); 

            const grouped = _.groupBy(preEvents, event => formatToLocalDate(event.t));

            const availableDates = Object.keys(grouped).sort((a, b) => {
                return new Date(a) - new Date(b);
            });

            this.groupedByDatePreEvents = grouped;
            this.availablePreDates = availableDates;
        },
        async fetchGroups(){
            await http.get(`07/market-groups`).then(res=>{
                if(res.status == 200){
                    this.marketGroups = res.data
                }
            })
        },
        async searchForFixtures({ search }) {
            if (!search) return []

            const queryParams = new URLSearchParams({ search }).toString()
            return http.get(`07/fixtures/search?${queryParams}`).then(response => {
                if (response.status === 200) {
                    const data = Array.isArray(response.data) ? response.data : []
                    const seen = new Set()
                    const unique = []
                    for (const ev of data) {
                        // the server searches every sport; drop the user's blocked ones
                        if (this.isFixtureBlocked(ev)) continue
                        if (ev?.i != null) {
                            if (seen.has(ev.i)) continue
                            seen.add(ev.i)
                        }
                        unique.push(ev)
                    }
                    return unique
                }
                return []
            }).catch(error => {
                console.error("searchForFixtures error:", error)
                return []
            })
        },
        async fetchSelections(eventIds, type = "pre", markets = [20890]) {
            const auth = useAuthStore()
            const { getUser } = storeToRefs(auth)
            const p = getUser.value?.p ?? 0
            const m = getUser.value?.mrg ?? 0

            return http.post("07/fixtures/selections", {
                [type]: eventIds,
                markets,
                p,
                m
            }).then(res => {
                if (res.status === 200 && Array.isArray(res.data?.[type])) {
                    this.selections = res.data[type].map(sel => ({
                        fi: Number(sel.fi ?? sel._id),
                         m: (sel.m || []).map(market => ({
                            ...market,
                            key: market.ui 
                        }))
                    }))
                    return this.selections
                }
                return []
            })
            .catch(err => {
                console.error("Error fetching selections", err)
                return []
            })
        },

        async fetchEventSelection(fi, s) {
            const auth = useAuthStore()
            const { getUser } = storeToRefs(auth)
            const p = getUser.value?.p ?? 0
            const m = getUser.value?.mrg ?? 0

            return http.get(`07/fixtures/${fi}/selections?s=${s}&p=${p}&m=${m}`)
                .then(res => {
                    if (res.status === 200 && res.data) {
                        let formatted = []
                        if (Array.isArray(res.data)) {
                            formatted = [{
                                fi: Number(fi),
                                  m: res.data.map(market => ({
                                    ...market,
                                    key: market.ui
                                }))
                            }]
                        }
                        this.selections = formatted
                        return formatted
                    }
                    return []
                })
                .catch(err => {
                    console.error("fetchEventSelection error", err)
                    return []
                })
            },


       async fetchAllMarkets() {
            return http.get(`07/markets`)
                .then(res => {
                    if (res.status === 200 && Array.isArray(res.data)) {
                        this.allMarkets = res.data.map(marketStr => this.parseMarketString(marketStr))
                    } else {
                        console.warn("fetchAllMarkets: data is not an array", res.data)
                        this.allMarkets = []
                    }
                })
                .catch(err => {
                    console.error("fetchAllMarkets error", err)
                })
        },

        async fetchAllCompetitions() {
            return http.get(`07/competitions`)
                .then(res => {
                    if (res.status === 200) {
                        this.allCompetitions = res.data.reduce((acc, c) => {
                        acc[c.i] = c.ord ?? 9999
                        return acc
                    }, {})
                    }
                })
            .catch(err => {
                console.error("fetchAllCompetitions error", err)
            })
        },
        async fetchScoreboards(eventIds){
              return http.post("07/fixtures/scoreboard", { events:eventIds }) .then(res => {
                if (res.status === 200) {
                    this.scoreboards = res.data
                    return res.data
                } else {
                    return []
                }
            })
            .catch(err => {
                console.error("Error fetching selections", err)
                return []
            })
        },
        updateOrAddFixture(data) {
            if (!data || typeof data !== "object") return

            const [eventId, value] = Object.entries(data)[0]

            if (typeof value === "number") {
                const fixture = this.fixtures.find(f => f.i == eventId)
                if (fixture) {
                    fixture.s = value
                    if (![0, 1].includes(Number(value))) {
                        this.fixtures = this.fixtures.filter(f => f.i != eventId)
                    }
                }
                return
            }

            if (typeof value === "string" && value.includes("|")) {
                const parsedFixture = this.parseFixtureString(value)

                const index = this.fixtures.findIndex(f => f.i == parsedFixture.i)
                if (index !== -1) {
                    this.fixtures.splice(index, 1, parsedFixture)
                } else {
                    this.fixtures.push(parsedFixture)
                }
            }
        },
    }
})
